import React, { useState, type MouseEventHandler } from 'react';
import { useAppDispatchActions, useAppState } from '../../context/AppStateProvider/useAppState';
import { wordpressFetch } from '../../utils/wordpress'; // Import wordpressFetch
import type { AmenityPopup, AppState } from '../../context/AppStateProvider/types';
import type { ApiContextType } from '../../context/ApiProvider/types';
import useSWR from 'swr';

type BookmarkProps = {
    amenity: AmenityPopup;
    state: AppState;
    api: ApiContextType;
    actions: ReturnType<typeof useAppDispatchActions>;
};

const fetcher = (url: string) => fetch(url).then((res) => res.json());

const Parking: React.FC<{ className?: string }> = ({ className }) => {
    const { state } = useAppState();
    const { itinerary, amenityTypes, amenities } = state;
    const { setSelectedAmenityTypes, setActiveEvent, setActiveAmenity } = useAppDispatchActions();

    const parkingID = itinerary?.parking;
    // Look for the parking amenity in our local state first
    const parking = amenities.find((amenity) => amenity.id === parkingID);

    const url = parkingID && !parking ? `https://api.mazemap.com/api/pois/${parkingID}/?srid=4326` : null;

    const { data: poiData } = useSWR(url, fetcher);

    // Determine display values based on what data source is available
    const displayTitle = parking?.title || poiData?.title || 'Looking for parking?';
    const displaySubtitle =
        parking || poiData ? 'Current selected parking' : 'View parking locations and save an area.';

    // Use MazeMap icon if available, otherwise fallback to your SVG
    const iconUrl = poiData?.iconId ? `https://api.mazemap.com/api/icons/${poiData.iconId}.svg` : null;

    return (
        <div
            className={`h-16 cursor-pointer items-center justify-start gap-4 self-stretch rounded-2xl bg-[var(--colour-charcoal-light)] px-4 py-3 outline outline-1 outline-offset-[-1px] outline-[var(--colour-grey-dark)] ${className}`}
            onClick={() => {
                setActiveEvent(null);

                // Filter types to find which ones are categorized as parking
                const parkingTypes = amenityTypes.filter((type) => type.is_parking === true).map((type) => type.slug);

                setSelectedAmenityTypes(parkingTypes);

                // If we have a selection, set it as active
                if (parkingID) {
                    setActiveAmenity(parkingID);
                }
            }}>
            <div className='relative flex h-8 w-8 items-center justify-center'>
                {iconUrl ? (
                    <img
                        src={iconUrl}
                        width={32}
                        height={32}
                        alt='Parking Icon'
                    />
                ) : (
                    <svg
                        viewBox='0 0 32 32'
                        width={32}
                        height={32}>
                        <defs>
                            <style>
                                {`.cls-1{fill:#fff;stroke-width:.74px;}.cls-1,.cls-2{stroke:#fff;stroke-miterlimit:10;}.cls-2{fill:#cc9900;stroke-width:1.5px;}`}
                            </style>
                        </defs>
                        <g id='Parking_Blue'>
                            <circle
                                className='cls-2'
                                cx='16'
                                cy='16'
                                r='14'
                            />
                            <path
                                className='cls-1'
                                d='m11.48,23.43v-14.12h5.33c.94,0,1.65.05,2.15.14.69.12,1.27.33,1.74.66.47.33.85.78,1.13,1.36.29.58.43,1.23.43,1.93,0,1.2-.38,2.22-1.15,3.05-.76.83-2.15,1.25-4.14,1.25h-3.62v5.74h-1.87Zm1.87-7.41h3.65c1.21,0,2.07-.23,2.57-.67.51-.45.76-1.08.76-1.9,0-.59-.15-1.1-.45-1.52-.3-.42-.69-.7-1.18-.83-.32-.08-.9-.12-1.74-.12h-3.61v5.05Z'
                            />
                        </g>
                    </svg>
                )}
            </div>
            <div className='inline-flex w-72 flex-col items-start justify-center gap-0.5'>
                <div className="justify-start self-stretch text-left font-['Effra'] text-base leading-4 font-bold text-[var(--colour-white)]">
                    {displayTitle}
                </div>
                <div className="justify-start self-stretch text-left font-['Effra'] text-sm leading-4 font-normal text-[var(--colour-white)]">
                    {displaySubtitle}
                </div>
            </div>
        </div>
    );
};

export const SaveParkingButton: React.FC<BookmarkProps> = ({ amenity, state, api, actions }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { itinerary, user } = state;
    const { itineraryMutate } = api;
    const { setIsRegistrationOpen } = actions;
    const [isInProgress, setIsInProgress] = useState(false);

    const isParked = itinerary?.parking == amenity.id;

    const handleSaveClick: MouseEventHandler<HTMLButtonElement> = async (e) => {
        e.stopPropagation();

        if (!user) {
            setIsRegistrationOpen(1);
            return;
        }

        setIsInProgress(true);

        if (isParked) {
            await wordpressFetch({
                path: `/wp-json/open-day-26/v1/itinerary/parking`,
                method: 'DELETE',
                credentials: 'include',
            });

            // --- GTM TRACKING START ---
            (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
            (window as any).dataCoreLayer.push({
                event: 'od_planner_button_click_parking',
                click_text: amenity.title,
                click_classes: 'remove',
            });
            // --- GTM TRACKING END ---
        } else {
            await wordpressFetch({
                path: '/wp-json/open-day-26/v1/itinerary/parking',
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ parking_id: amenity.id }),
            });

            // --- GTM TRACKING START ---
            (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
            (window as any).dataCoreLayer.push({
                event: 'od_planner_button_click_parking',
                click_text: amenity.title,
                click_classes: 'saved',
            });
            // --- GTM TRACKING END ---
        }
        await itineraryMutate();
        setIsInProgress(false);
    };

    let buttonContent;
    let buttonClasses =
        'cursor-pointer rounded-[5px] px-[12px] py-[10px] leading-[14px] text-[14px] self-end transition-colors duration-200 border flex items-center gap-2 font-medium';

    if (isInProgress) {
        buttonContent = isParked ? 'Removing ...' : 'Saving ...';
        buttonClasses +=
            ' border-[var(--colour-charcoal-dark)] bg-[var(--colour-charcoal-dark)] text-[var(--colour-white)]';
    } else if (isParked) {
        if (isHovered) {
            buttonContent = (
                <>
                    <svg
                        width='21'
                        height='2'
                        viewBox='0 0 21 2'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M19.8531 1.42959C20.0102 1.27254 20.0984 1.05953 20.0984 0.837433C20.0984 0.615332 20.0102 0.402327 19.8531 0.245278C19.6961 0.0882292 19.4831 0 19.261 0H10.8866H9.21176H0.837433C0.615332 0 0.402328 0.0882292 0.245279 0.245278C0.0882294 0.402327 0 0.615332 0 0.837433C0 1.05953 0.0882294 1.27254 0.245279 1.42959C0.402328 1.58664 0.615332 1.67487 0.837433 1.67487H9.21176H10.8866H19.261C19.4831 1.67487 19.6961 1.58664 19.8531 1.42959Z'
                            fill='#0B0B0B'
                        />
                    </svg>
                    Remove
                </>
            );
            buttonClasses += ' border-[var(--colour-gold)] bg-[var(--colour-white)] text-[var(--colour-charcoal-dark)]';
        } else {
            buttonContent = (
                <>
                    <svg
                        width='19'
                        height='13'
                        viewBox='0 0 19 13'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M17.8439 1.23354L6.28886 12.7886C6.22179 12.8557 6.14214 12.909 6.05447 12.9453C5.96679 12.9817 5.87282 13.0004 5.77791 13.0004C5.683 13.0004 5.58903 12.9817 5.50136 12.9453C5.41368 12.909 5.33403 12.8557 5.26696 12.7886L0.211642 7.73324C0.0761298 7.59773 0 7.41393 0 7.22229C0 7.03065 0.0761298 6.84685 0.211642 6.71134C0.347154 6.57583 0.530947 6.4997 0.72259 6.4997C0.914233 6.4997 1.09803 6.57583 1.23354 6.71134L5.77791 11.2566L16.822 0.211641C16.9575 0.0761294 17.1413 -1.42785e-09 17.3329 0C17.5246 1.42785e-09 17.7084 0.0761294 17.8439 0.211641C17.9794 0.347153 18.0555 0.530947 18.0555 0.72259C18.0555 0.914233 17.9794 1.09803 17.8439 1.23354Z'
                            fill='#0B0B0B'
                        />
                    </svg>
                    Saved
                </>
            );
            buttonClasses += ' border-[var(--colour-gold)] bg-[var(--colour-gold)] text-[var(--colour-charcoal-dark)]';
        }
    } else {
        // Not planned
        buttonContent = 'Save for later';
        if (isHovered) {
            buttonClasses +=
                ' border-[var(--colour-charcoal-dark)] bg-[var(--colour-white)] text-[var(--colour-charcoal-dark)]';
        } else {
            buttonClasses +=
                ' border-[var(--colour-charcoal-dark)] bg-[var(--colour-charcoal-dark)] text-[var(--colour-white)]';
        }
    }

    return (
        <button
            onClick={handleSaveClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={buttonClasses}
            disabled={isInProgress}>
            {buttonContent}
        </button>
    );
};

export default Parking;
